import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { getNetworkRole, type NetworkRole } from "../api/hospitals";

type GrandmasterExperienceContextValue = {
  /** True only for organization grandmaster accounts — unlocks uncommitted/new UX. */
  isGrandmasterExperience: boolean;
  /** One login posting locum shifts to many clinic sites — stays on the classic portal. */
  isClinicNetworkExperience: boolean;
  loading: boolean;
  networkRole: NetworkRole | null;
  refresh: () => Promise<void>;
};

const LOGIN_ROLE_KEY = "login-network-role";

const readPinnedLoginRole = (): NetworkRole | null => {
  try {
    const raw = sessionStorage.getItem(LOGIN_ROLE_KEY) || localStorage.getItem(LOGIN_ROLE_KEY);
    return raw ? (JSON.parse(raw) as NetworkRole) : null;
  } catch {
    return null;
  }
};

const writePinnedLoginRole = (role: NetworkRole) => {
  try {
    const raw = JSON.stringify(role);
    sessionStorage.setItem(LOGIN_ROLE_KEY, raw);
    localStorage.setItem(LOGIN_ROLE_KEY, raw);
  } catch {
    // ignore
  }
};

const GrandmasterExperienceContext =
  createContext<GrandmasterExperienceContextValue | null>(null);

/**
 * After login, loads /network-role once. Non-grandmasters keep the classic
 * StatHospital experience (pre–uncommitted features). Grandmasters see the
 * full local/new product surface.
 */
export function GrandmasterExperienceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const loginStatus = useSelector(
    (state: any) => Boolean(state.userInfo?.loginStatus),
  );
  const [networkRole, setNetworkRole] = useState<NetworkRole | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!loginStatus) {
      setNetworkRole(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const role = await getNetworkRole();
      const pinned = readPinnedLoginRole();
      if (role?.grandmaster && !role.clinicNetwork) {
        writePinnedLoginRole(role);
        setNetworkRole(role);
        return;
      }
      if (pinned?.grandmaster && !pinned.clinicNetwork) {
        setNetworkRole(pinned);
        return;
      }
      if (role) writePinnedLoginRole(role);
      setNetworkRole(role);
    } catch {
      setNetworkRole(null);
    } finally {
      setLoading(false);
    }
  }, [loginStatus]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<GrandmasterExperienceContextValue>(
    () => ({
      isGrandmasterExperience: Boolean(networkRole?.grandmaster),
      isClinicNetworkExperience:
        Boolean(networkRole?.clinicNetwork || networkRole?.experience === "clinic_network") &&
        !networkRole?.grandmaster,
      loading: loginStatus ? loading : false,
      networkRole,
      refresh,
    }),
    [networkRole, loading, loginStatus, refresh],
  );

  return (
    <GrandmasterExperienceContext.Provider value={value}>
      {children}
    </GrandmasterExperienceContext.Provider>
  );
}

export function useGrandmasterExperience(): GrandmasterExperienceContextValue {
  const ctx = useContext(GrandmasterExperienceContext);
  if (!ctx) {
    return {
      isGrandmasterExperience: false,
      isClinicNetworkExperience: false,
      loading: false,
      networkRole: null,
      refresh: async () => undefined,
    };
  }
  return ctx;
}

export function useGrandmasterExperienceOptional(): GrandmasterExperienceContextValue | null {
  return useContext(GrandmasterExperienceContext);
}
